import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { HelpCircle, Mail, KeyRound, Wrench, MessageSquare } from "lucide-react";

const SUPPORT_EMAIL = "copyengine@marketenginuity.com";

const mailto = (subject: string) =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;

const TOPICS = [
  {
    icon: KeyRound,
    iconWrap: "bg-amber-100 text-amber-600",
    title: "Password resets",
    body: "Locked out? Send a reset request and an admin will reply with a temporary password.",
    subject: "Password reset request",
  },
  {
    icon: MessageSquare,
    iconWrap: "bg-emerald-100 text-emerald-600",
    title: "Using the tools",
    body: "Questions about VBRs, the Copy Engine, CNAs, or your sponsor workbench? Ask away.",
    subject: "AI Sales Toolkit question",
  },
  {
    icon: Wrench,
    iconWrap: "bg-sky-100 text-sky-600",
    title: "Something not working?",
    body: "Tell us what you were doing and what you saw. Screenshots help a lot.",
    subject: "AI Sales Toolkit issue report",
  },
];

// Help & Support dialog, opened from the header's question-mark button on
// every page. Everything points at the team inbox via mailto; the app sends
// no email itself (no email provider is configured).
export default function HelpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-emerald-600" />
            Help &amp; Support
          </DialogTitle>
          <DialogDescription>
            Questions, feedback, or something misbehaving? We are one email away.
          </DialogDescription>
        </DialogHeader>

        {/* Support inbox */}
        <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white p-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">Support inbox</p>
          <p className="text-lg font-semibold text-gray-900 break-all" data-testid="text-support-email">{SUPPORT_EMAIL}</p>
          <p className="text-xs text-gray-500 mt-1.5">We read everything and usually reply within one business day.</p>
          <Button
            size="sm"
            className="mt-3"
            onClick={() => { window.location.href = mailto("AI Sales Toolkit help request"); }}
            data-testid="button-email-support"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email support
          </Button>
        </div>

        {/* Common topics */}
        <div className="space-y-2.5">
          {TOPICS.map((t) => (
            <a
              key={t.title}
              href={mailto(t.subject)}
              className="flex items-start gap-3 rounded-lg border border-gray-100 p-3 hover:bg-gray-50 transition-colors"
            >
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${t.iconWrap}`}>
                <t.icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{t.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.body}</p>
              </div>
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
